import { HomeLoading } from "@/components/home-loading";
import logoColors from "@/assets/logo-colors.png";
import gorgiasLogo from "@/assets/integrations/gorgias.png";
import { useAuth } from "@babackapp/auth-client/hooks";
import useAllViewerShops from "@babackapp/private-api-client/client/use-all-viewer-shops";
import useFinalizeGorgiasIntegration from "@babackapp/private-api-client/client/use-finalize-gorgias-integration";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { useState } from "react";
import { Navigate } from "react-router-dom";

function constructRedirectUrl(token: string, account: string) {
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append("token", token);
  urlSearchParams.append("account", account);
  return `/gorgias/connect?${urlSearchParams.toString()}`;
}

function LogoConnection() {
  return (
    <div className="flex items-center justify-center gap-4 mb-8">
      <div className="flex items-center justify-center w-16 h-16 bg-white rounded-xl shadow-md p-2">
        <img
          src={logoColors}
          alt="Baback"
          className="h-10 w-10 object-contain"
        />
      </div>
      <div className="flex items-center gap-1">
        <div className="w-8 h-0.5 bg-gradient-to-r from-purple-400 to-purple-300 rounded" />
        <div className="w-3 h-3 rounded-full border-2 border-purple-400 bg-white flex items-center justify-center">
          <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
        </div>
        <div className="w-8 h-0.5 bg-gradient-to-l from-orange-400 to-orange-300 rounded" />
      </div>
      <div className="flex items-center justify-center w-16 h-16 bg-white rounded-xl shadow-md p-2">
        <img
          src={gorgiasLogo}
          alt="Gorgias"
          className="h-10 w-10 object-contain"
        />
      </div>
    </div>
  );
}

export default function GorgiasConnectPage() {
  const t = useTranslation("gorgiasConnect");
  const auth = useAuth();
  const { data: shops, isPending: isShopsLoading } = useAllViewerShops();
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = new URLSearchParams(window.location.search).get("token");
  const account = new URLSearchParams(window.location.search).get("account");

  const finalizeMutation = useFinalizeGorgiasIntegration({
    onSuccess: () => {
      setIsSuccess(true);
      // Redirect to Gorgias after a short delay
      setTimeout(() => {
        window.location.href = `https://${account}.gorgias.com/app/home`;
      }, 2000);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Missing required params
  if (!token || !account) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-4">
        <LogoConnection />
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("invalidLink.title")}</CardTitle>
            <CardDescription>{t("invalidLink.description")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Loading auth state
  if (auth.isAuthenticating) return <HomeLoading />;

  // Not authenticated - redirect to sign in
  if (!auth.isAuthenticated) {
    const redirectUrl = constructRedirectUrl(token, account);
    return (
      <Navigate
        to={`/auth/sign-in?redirectTo=${encodeURIComponent(redirectUrl)}`}
        replace
      />
    );
  }

  // Loading shops
  if (isShopsLoading) return <HomeLoading />;

  // No shops found
  if (!shops || shops.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-4">
        <LogoConnection />
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("noShops.title")}</CardTitle>
            <CardDescription>{t("noShops.description")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-4">
        <LogoConnection />
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-green-600">
              {t("success.title")}
            </CardTitle>
            <CardDescription>{t("success.description")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const handleConnect = () => {
    if (!selectedShopId || !token) return;
    finalizeMutation.mutate({ shopId: selectedShopId, token });
  };

  const selectedShop = shops.find((shop) => shop.id === selectedShopId);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-4">
      <LogoConnection />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>{t("title")}</CardTitle>
          <CardDescription>
            {t("subtitle")} <strong>{account}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                {t("selectShop")}
              </label>
              <Select
                value={selectedShopId ?? undefined}
                onValueChange={setSelectedShopId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("selectShopPlaceholder")}>
                    {selectedShop && (
                      <div className="flex flex-col items-start">
                        <span>{selectedShop.name}</span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {shops.map((shop) => (
                    <SelectItem key={shop.id} value={shop.id}>
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{shop.name}</span>
                        {shop.shopifyDomain && (
                          <span className="text-xs text-gray-500">
                            {shop.shopifyDomain}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleConnect}
              disabled={!selectedShopId || finalizeMutation.isPending}
              className="w-full"
            >
              {finalizeMutation.isPending ? t("connecting") : t("connect")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
